import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

function getColLetter(colNum) {
    let temp = '';
    while (colNum > 0) {
        let mod = (colNum - 1) % 26;
        temp = String.fromCharCode(65 + mod) + temp;
        colNum = Math.floor((colNum - mod) / 26);
    }
    return temp;
}

export async function exportBranchWiseLedger({
    data = [],
    fileName = 'BranchWiseLedger.xlsx',
    sheetName = 'Ledger',
    selectedDate,
    title
}) {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet(sheetName);

    const branches = data.map(d => d.locationName);
    const totalColumns = 2 + branches.length + 1;

    // ===== Heading row =====
    const headingRow = sheet.addRow(['General Ledger Current Balance' + (title ? ` - ${title}` : '') + (selectedDate ? ` - ${selectedDate}` : '')]);
    headingRow.font = { bold: true, size: 16, color: { argb: 'FFFFFF' } };
    sheet.mergeCells(`A${headingRow.number}:${getColLetter(totalColumns)}${headingRow.number}`);
    headingRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '4472C4' } };

    // ===== Printed Date row =====
    const printedDateRow = sheet.addRow([`Printed Date: ${new Date().toLocaleString()}`]);
    sheet.mergeCells(`A${printedDateRow.number}:${getColLetter(totalColumns)}${printedDateRow.number}`);
    printedDateRow.font = { italic: true };
    printedDateRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DDEBF7' } };
    printedDateRow.alignment = { horizontal: 'left' };

    sheet.addRow([]); // blank row

    // ===== Header row =====
    const headerRow = ['Ledger Code', 'Ledger Name', ...branches, 'GL Total'];
    const headerExcelRow = sheet.addRow(headerRow);
    headerExcelRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '4472C4' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = {
            top: { style: 'thin' },
            bottom: { style: 'thin' },
            left: { style: 'thin' },
            right: { style: 'thin' }
        };
    });

    // ===== Collect all unique ledger codes across branches =====
    const ledgerMap = new Map();
    data.forEach(branch => {
        branch.ledgerCodeAndAmountDtoList.forEach(item => {
            const key = item.ledgerCode + '-' + item.ledgerName;
            if (!ledgerMap.has(key)) {
                ledgerMap.set(key, { ledgerCode: item.ledgerCode, ledgerName: item.ledgerName });
            }
        });
    });

    // Convert Map to array and sort numerically by ledgerCode
    const ledgerArray = Array.from(ledgerMap.values());
    ledgerArray.sort((a, b) => Number(a.ledgerCode) - Number(b.ledgerCode));

    // ===== Fill data rows =====
    const dataStartRow = sheet.lastRow.number + 1;
    ledgerArray.forEach(value => {
        const rowValues = [value.ledgerCode, value.ledgerName];
        let glTotal = 0;

        branches.forEach(locationName => {
            const branch = data.find(b => b.locationName === locationName);
            const ledgerItem = branch.ledgerCodeAndAmountDtoList.find(li => li.ledgerCode === value.ledgerCode);
            const amount = ledgerItem ? ledgerItem.amount : 0;
            rowValues.push(amount);
            glTotal += amount;
        });

        rowValues.push(glTotal);
        const addedRow = sheet.addRow(rowValues);

        // Styling each cell
        addedRow.eachCell((cell, colNum) => {
            if (colNum > 2) cell.numFmt = '#,##0.00';
            cell.border = {
                top: { style: 'thin' },
                bottom: { style: 'thin' },
                left: { style: 'thin' },
                right: { style: 'thin' }
            };
            // Alternate row fill
            if ((addedRow.number - dataStartRow) % 2 === 0 && colNum > 2) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F2F2F2' } };
            }
        });
    });

    const dataEndRow = sheet.lastRow.number;

    // ===== Branch-wise totals row =====
    const totalRowValues = ['Total', ''];
    for (let i = 3; i <= 2 + branches.length; i++) {
        const colLetter = getColLetter(i);
        totalRowValues.push({ formula: `SUM(${colLetter}${dataStartRow}:${colLetter}${dataEndRow})` });
    }
    const glTotalColLetter = getColLetter(headerRow.length);
    totalRowValues.push({ formula: `SUM(${glTotalColLetter}${dataStartRow}:${glTotalColLetter}${dataEndRow})` });

    const totalRow = sheet.addRow(totalRowValues);
    totalRow.eachCell((cell, colNum) => {
        cell.font = { bold: true };
        if (colNum > 2) cell.numFmt = '#,##0.00';
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D9E1F2' } };
        cell.border = {
            top: { style: 'thin' },
            bottom: { style: 'double' },
            left: { style: 'thin' },
            right: { style: 'thin' }
        };
    });

    // ===== Auto-width columns =====
    sheet.columns.forEach((column, index) => {
        let maxLength = 10;
        column.eachCell({ includeEmpty: true }, cell => {
            const cellValue = cell.value ? cell.value.toString() : '';
            maxLength = Math.max(maxLength, cellValue.length);
        });

        // Limit width for the first column
        if (index === 0) {
            column.width = Math.min(maxLength + 2, 20); // cap at 20
        } else {
            column.width = maxLength + 2;
        }
    });

    // Freeze headers
    sheet.views = [{ state: 'frozen', xSplit: 2, ySplit: headerExcelRow.number }];

    // Export Excel
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, fileName);
}
